import { type PageProps, STATUS } from "@types";
import type { Meta } from "@layouts/page";
import Page from "@layouts/page";
import { useTranslations } from "next-intl";
import courseApi from "@api/course";
import { SITE_URL } from "@utils/constants";
import { useAppContext } from "@context/app";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import { useRouter } from "next/router";
import { FC, useEffect, useState } from "react";
import NotFound from "pages/404";
import Head from "next/head";
import { getBasePath } from "@utils/helpers";
import commonApi from "@api/common";
import { toFarsiNumber } from "@utils/digits";
import NonAccessSkeleton from "@modules/course/non-access/non-access-skeleton";
import { getUrlByCourseType } from "@utils/course";
import { AccessProvider } from "@templates/course/context";
import PackSkeleton from "@modules/course/pack-non-access/loading/pack-skeleton";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import dynamic from "next/dynamic";
import AccessSkeleton from "@modules/course/access/access-skeleton";
import NonAccess from "./course/non-access";
import {
  breadCrumbObject,
  courseObject,
  creativeWorkSeries,
  imageObject,
  itemPage,
} from "@utils/jsonLd";
import { useLayoutContext } from "@context/layout";
import { AppBanner } from "@shared/components";
import { useIsoMorphicEffect } from "@shared/hooks/useIsoMorphicEffect";

const AdminHeaderCourse = dynamic(
  () => import("@modules/course/admin-header-course"),
  { ssr: false }
);

const Access = dynamic(() => import("@templates/course/access"), {
  ssr: false,
  loading: () => <AccessSkeleton />,
});
const PackNonAccess = dynamic(() => import("./course/pack-non-access"), {
  ssr: false,
  loading: () => <PackSkeleton />,
});

interface Props extends PageProps {
  courseSlug?: string;
}

const CoursePage: FC<Props> = ({ courseSlug, is404 }) => {
  const {
    setShowFestivalBanner,
    setHideTopbar,
    setHideFooter,
    setHideBottomNavigation,
    hideTopbar,
    hideFooter,
    hideBottomNavigation,
  } = useAppContext();
  const isMobile = useIsMobile();
  const t = useTranslations("meta");
  const router = useRouter();
  const { slug } = router.query;
  const mode =
    typeof router.query.mode === "string" ? router.query.mode : undefined;

  const [hasAccess, setHasAccess] = useState(false);

  const {
    data: course = null,
    error,
    mutate,
  } = useRequestImmutable(
    courseApi.getCourseData(slug as string, {
      showProduct: 1,
      mode,
    }),
    { revalidateOnMount: true }
  );

  const loadedBySku = course?.product && course.product.slug !== slug;
  const { isAdmin, fetstivalBanner } = useLayoutContext();

  const { data: access } = useRequestImmutable(
    isAdmin ? commonApi.getAccessMenu() : null,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (course && course.product) {
      setHasAccess(Boolean(course.user.is_buy));

      const redirectedRoute = getUrlByCourseType(
        course.product.attribute_type,
        course.product.webinar?.webinar_status,
        course.product.slug
      );
      const fullRoute = window.location.href || "";
      const sectionId =
        typeof fullRoute === "string" && fullRoute?.includes?.("#")
          ? (fullRoute.split("#") || []).reverse()?.[0]
          : null;
      const previousQueryParams = window.location.search;
      if (redirectedRoute !== window.location.pathname) {
        const redirectingRoute = sectionId
          ? `${redirectedRoute}#${sectionId}${previousQueryParams}`
          : redirectedRoute + previousQueryParams;
        router.replace(redirectingRoute);
      }
    }
  }, [course]);

  useIsoMorphicEffect(() => {
    // handles render layout
    if (course) {
      if (Boolean(course?.user?.is_buy)) {
        setShowFestivalBanner(false);
        setHideTopbar(true);
        setHideFooter(true);
        setHideBottomNavigation(true);
      } else if (isMobile) {
        setHideTopbar(true);
        setHideBottomNavigation(true);
      }
    }
    return () => {
      setHideTopbar(false);
      setHideFooter(false);
      setHideBottomNavigation(false);
    };
  }, [course, isMobile, hideTopbar, hideFooter, hideBottomNavigation]);

  useEffect(() => {
    if (loadedBySku) {
      const search = window.location.search;
      const courseLink =
        getBasePath([]) + "/courses/" + course.product.slug + search;
      window.history.replaceState(null, "", courseLink);
    }
  }, [loadedBySku]);

  useEffect(() => {
    if (router.query.registered === "1") mutate();
  }, [router.query]);

  if (is404 || error?.error?.code === 404) return <NotFound />;

  if (!course?.product)
    return courseSlug === "packages" ? <PackSkeleton /> : <NonAccessSkeleton />;

  const isPack = course.product.package?.is_huge;

  const hasReportAccess = access?.data.page_access?.course_detail;
  const hasViewAccess = access?.data.page_access?.access_page;
  const isPublished = course?.product?.status === STATUS.PUBLISHED;

  const meta: Meta = {
    title: toFarsiNumber(course.product.seo_title || t("title")),
    description: toFarsiNumber(course.product.seo_metadesc || t("desc")),
    image:
      course.product.png_featured_image ||
      course.product.png_cover ||
      course.product.png_image ||
      course.product.svg_image,
    isAvailable: true,
    releaseDate: course.product.created_at,
    price: course.product.price,
    category: course.product.categories?.map((x: any) => x.slug)?.join("/"),
    instructor: course.product.instructors
      ?.map((x: any) => `${x.first_name} ${x.last_name}`)
      ?.join("/"),
    isHideGoftino: hasAccess && !isPack,
  };

  const firstPriorityPoster =
    course?.product?.svg_featured_image ||
    course?.product?.png_featured_image ||
    course?.product?.svg_image;
  const festivalBannerIsLcp = Boolean(fetstivalBanner?.is_active);

  return (
    <Page meta={meta}>
      <Head>
        {!festivalBannerIsLcp && firstPriorityPoster ? (
          <link
            rel="preload"
            as="image"
            href={firstPriorityPoster}
            fetchPriority="high"
          />
        ) : null}
        {course && (
          <>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(
                  breadCrumbObject([
                    {
                      id: SITE_URL + "/explore",
                      name: "همه آموزش‌ها",
                    },
                    {
                      id: SITE_URL + "/courses/" + course.product.slug,
                      name: course.product.title,
                    },
                  ])
                ),
              }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(courseObject(course)),
              }}
            />
          </>
        )}
        {course && course.product.rating.total && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(creativeWorkSeries(course)),
            }}
          />
        )}
        {course && (
          <>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(
                  itemPage({
                    url: SITE_URL + "/courses/" + course.product.slug,
                    imageUrl: course.product.svg_image,
                    name: course.product.seo_title,
                    mainContentSelector: ".courseItemPageSelector",
                    datePublished: course.product.published_at,
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: course.product.rating.rate,
                      ratingCount: course.product.rating.total,
                      bestRating: 5,
                      worstRating: 1,
                      ...(course.product.commentCount
                        ? { reviewCount: course.product.commentCount }
                        : {}),
                    },
                    keywords: course.product.tags
                      ?.map((item: any) => item.name)
                      .join(","),
                  })
                ),
              }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(
                  imageObject({
                    caption:
                      course.product.seo_metadesc ||
                      course.product.description_data.description,
                    contentUrl:
                      course.product.png_featured_image ||
                      course.product.svg_featured_image ||
                      course.product.png_image ||
                      course.product.svg_image,
                    width: "715",
                    height: "400",
                    url: SITE_URL + "/courses/" + course.product.slug,
                    isPartOf: SITE_URL + "/courses/" + course.product.slug,
                    uploadDate: course.product.published_at,
                  })
                ),
              }}
            />
          </>
        )}
      </Head>
      {isAdmin ? (
        <AdminHeaderCourse
          isPublished={isPublished}
          mutate={mutate}
          slug={slug}
          id={course.product.id}
          type={course.product.attribute_type}
          hasAccess={hasAccess}
          hasReportAccess={hasReportAccess}
          hasViewAccess={hasViewAccess}
          hasMarkdown
          productSku={course?.product?.sku}
        />
      ) : null}
      {hasAccess && <AppBanner />}
      <AccessProvider data={course} mutate={mutate}>
        {hasAccess && !isPack ? (
          <Access />
        ) : isPack ? (
          <PackNonAccess />
        ) : (
          <NonAccess />
        )}
      </AccessProvider>
    </Page>
  );
};

export default CoursePage;
